import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import PrintCanvas from './print/PrintCanvas'
import { SimpleViewport } from './print/SimpleViewport'
import { FullSchemaRenderer } from './print/FullSchemaRenderer'

// API функции для печати (копируем из старого файла)
const getPrintSheet = async (sheetId: number) => {
  const response = await fetch(`/api/print/sheets/${sheetId}`)
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch sheet')
  return data.data
}

const getPrintViewports = async (sheetId: number) => {
  const response = await fetch(`/api/print/viewports?sheet_id=${sheetId}`)
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch viewports')
  return data.data
}

const getPrintStamps = async () => {
  const response = await fetch('/api/print/stamps')
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch stamps')
  return data.data
}

const getPrintPresets = async () => {
  const response = await fetch('/api/print/presets')
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to fetch presets')
  return data.data
}

const updatePrintSheet = async ({ id, data }: { id: number; data: any }) => {
  const response = await fetch(`/api/print/sheets/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  const result = await response.json()
  if (!result.success) throw new Error(result.error || 'Failed to update sheet')
  return result.data
}

const updatePrintViewport = async ({ id, data }: { id: number; data: any }) => {
  const response = await fetch(`/api/print/viewports/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  
  const result = await response.json()
  
  if (!result.success) {
    throw new Error(result.error || 'Failed to update viewport')
  }
  
  return result.data
}

const deletePrintViewport = async (viewportId: number) => {
  const response = await fetch(`/api/print/viewports/${viewportId}`, {
    method: 'DELETE'
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to delete viewport')
  return data
}

// const updatePrintSheetStamp = async ({ sheetId, stampId, values }: { sheetId: number; stampId: number; values: any }) => {
//   const response = await fetch(`/api/print/sheets/${sheetId}/stamp`, {
//     method: 'PUT',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ stamp_id: stampId, values_json: values })
//   })
//   const data = await response.json()
//   if (!data.success) throw new Error(data.error || 'Failed to update stamp')
//   return data.data
// }

// const generatePDF = async ({ 
//   projectId, 
//   sheetId, 
//   htmlContent, 
//   filename, 
//   dpi 
// }: { 
//   projectId: number; 
//   sheetId: number; 
//   htmlContent: string; 
//   filename: string; 
//   dpi: number 
// }) => {
//   const response = await fetch('/api/print/generate-pdf', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ project_id: projectId, sheet_id: sheetId, html_content: htmlContent, filename, dpi })
//   })
//   const data = await response.json()
//   if (!data.success) throw new Error(data.error || 'Failed to generate PDF')
//   return data.data
// }

const savePreset = async ({ name, sheetId }: { name: string; sheetId: number }) => {
  const response = await fetch('/api/print/save-preset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, sheet_id: sheetId })
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to save preset')
  return data.data
}

const applyPreset = async ({ sheetId, presetId }: { sheetId: number; presetId: number }) => {
  const response = await fetch('/api/print/apply-preset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sheet_id: sheetId, preset_id: presetId })
  })
  const data = await response.json()
  if (!data.success) throw new Error(data.error || 'Failed to apply preset')
  return data.data
}

// Типы
interface PrintSheet {
  id: number
  name: string
  format: string
  orientation: 'portrait' | 'landscape'
  width_mm: number
  height_mm: number
  margin_mm: string
  project_id: number
  created_at: string
  stamp_id?: number
  stamp_values_json?: string
}

interface PrintViewport {
  id: number
  name?: string
  screen_x: number
  screen_y: number
  screen_width: number
  screen_height: number
  scroll_x?: number
  scroll_y?: number
  zoom?: number
  z_index: number
  schema_type: string
  block_id?: number
}

interface PrintStamp {
  id: number
  name: string
  template_html: string
  width_mm: number
  height_mm: number
}

// interface PrintPreset {
//   id: number
//   name: string
//   sheet_data: any
//   created_at: string
// }

// Константы
const PAPER_FORMATS: Record<string, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A3: { width: 297, height: 420 },
  A2: { width: 420, height: 594 },
  A1: { width: 594, height: 841 },
  A0: { width: 841, height: 1189 }
}

const DPI_OPTIONS = [
  { value: 72, label: '72 DPI (быстро)' },
  { value: 150, label: '150 DPI (средне)' },
  { value: 300, label: '300 DPI (качество)' }
]

export default function PrintSheetEditor() {
  const { projectId, sheetId } = useParams()
  const queryClient = useQueryClient()
  
  // States
  const [selectedDPI, setSelectedDPI] = useState(150)
  const [editingViewport, setEditingViewport] = useState<PrintViewport | null>(null)
  const [selectedStamp, setSelectedStamp] = useState<PrintStamp | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [isSavePresetModalOpen, setIsSavePresetModalOpen] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [localViewports, setLocalViewports] = useState<PrintViewport[]>([])
  const [schemaBounds, setSchemaBounds] = useState<Record<number, { minX: number; minY: number; maxX: number; maxY: number }>>({})
  const autoFittedRef = useRef<Set<number>>(new Set())

  // Переключатель режима взаимодействия для каждого viewport: false (редактирование рамки), true (скролл внутри)
  const [viewportScrollMode, setViewportScrollMode] = useState<Record<number, boolean>>({})
  
  // Canvas zoom state
  const [canvasZoom, setCanvasZoom] = useState(1)
  const [canvasCenter, setCanvasCenter] = useState({ x: 0, y: 0 })
  
  // Global zoom mode: true = zoom viewports, false = zoom canvas
  const [globalZoomMode, setGlobalZoomMode] = useState(false)
  const isNewArchitecture = window.location.hash === '#new-architecture'
  
  // Canvas zoom and pan persistence
  const getCanvasStorageKey = () => `print-canvas-${projectId}-${sheetId}`
  
  // Viewport persistence for new architecture
  const getViewportStorageKey = (viewportId: number) => `print-viewport-${projectId}-${sheetId}-${viewportId}`
  
  // Save viewport to localStorage
  const saveViewportToStorage = useCallback((viewportId: number, viewport: { scroll_x: number; scroll_y: number; zoom: number }) => {
    if (!isNewArchitecture) return
    const key = getViewportStorageKey(viewportId)
    localStorage.setItem(key, JSON.stringify(viewport))
  }, [isNewArchitecture, projectId, sheetId])
  
  // Restore viewport from localStorage
  const restoreViewportFromStorage = useCallback((viewportId: number) => {
    if (!isNewArchitecture) return null
    const key = getViewportStorageKey(viewportId)
    const saved = localStorage.getItem(key)
    if (saved) {
      try {
        return JSON.parse(saved) as { scroll_x: number; scroll_y: number; zoom: number }
      } catch (e) {
        console.error('Error parsing saved viewport:', e)
        return null
      }
    }
    return null
  }, [isNewArchitecture, projectId, sheetId])
  
  // Save canvas state to localStorage (simple function, no useEffect)
  const saveCanvasState = useCallback((zoom: number, center: { x: number; y: number }) => {
    // Don't save if we're currently restoring from localStorage
    if (isRestoringCanvas.current) return
    
    const key = getCanvasStorageKey()
    localStorage.setItem(key, JSON.stringify({ zoom, center }))
  }, [getCanvasStorageKey])
  
  // Restore canvas state from localStorage
  const restoreCanvasState = useCallback(() => {
    const key = getCanvasStorageKey()
    const saved = localStorage.getItem(key)
    if (saved) {
      try {
        const { zoom, center } = JSON.parse(saved)
        if (typeof zoom === 'number' && typeof center === 'object' && 
            typeof center.x === 'number' && typeof center.y === 'number') {
          isRestoringCanvas.current = true
          setCanvasZoom(zoom)
          setCanvasCenter(center)
          // Reset flag after a short delay
          setTimeout(() => {
            isRestoringCanvas.current = false
          }, 100)
        }
      } catch (e) {
        console.error('Error parsing saved canvas state:', e)
      }
    }
  }, [getCanvasStorageKey])
  
  // Viewport dragging state
  const [draggingViewport, setDraggingViewport] = useState<number | null>(null)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [viewportStartPos, setViewportStartPos] = useState({ x: 0, y: 0 })
  const viewportSaveTimers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const isRestoringCanvas = useRef(false)
  
  // Viewport resizing state
  const [resizingViewport, setResizingViewport] = useState<number | null>(null)
  const [resizeHandle, setResizeHandle] = useState<string | null>(null)
  const [viewportStartSize, setViewportStartSize] = useState({ width: 0, height: 0 })
  
  const toggleViewportScrollMode = (id: number) => {
    setViewportScrollMode(prev => ({ ...prev, [id]: !prev[id] }))
  }

  // Canvas zoom and pan handlers
  const handleCanvasWheel = (e: Event) => {
    const wheelEvent = e as WheelEvent
    
    // Get mouse position relative to canvas container
    const canvasContainer = document.querySelector('.canvas-container') as HTMLElement
    if (!canvasContainer) return
    
    const rect = canvasContainer.getBoundingClientRect()
    const mouseX = wheelEvent.clientX - rect.left
    const mouseY = wheelEvent.clientY - rect.top
    
    // Check if mouse is over any viewport
    const viewportUnderMouse = localViewports.find(viewport => {
      const viewportX = viewport.screen_x * canvasZoom + canvasCenter.x
      const viewportY = viewport.screen_y * canvasZoom + canvasCenter.y
      const viewportWidth = viewport.screen_width * canvasZoom
      const viewportHeight = viewport.screen_height * canvasZoom
      
      return mouseX >= viewportX && mouseX <= viewportX + viewportWidth &&
             mouseY >= viewportY && mouseY <= viewportY + viewportHeight
    })
    
    if (viewportUnderMouse) {
      // Mouse is over a viewport
      if (globalZoomMode) {
        // In global zoom mode, zoom the viewport content around mouse position
        const delta = wheelEvent.deltaY > 0 ? 0.9 : 1.1
        const oldZoom = viewportUnderMouse.zoom || 1
        const newZoom = Math.max(0.1, Math.min(4, oldZoom * delta))
        
        // Calculate mouse position relative to viewport
        const viewportX = viewportUnderMouse.screen_x * canvasZoom + canvasCenter.x
        const viewportY = viewportUnderMouse.screen_y * canvasZoom + canvasCenter.y
        const relativeMouseX = mouseX - viewportX
        const relativeMouseY = mouseY - viewportY
        
        // Calculate zoom center offset - simple and reliable
        const zoomRatio = newZoom / oldZoom
        let newScrollX = (viewportUnderMouse.scroll_x || 0) - (relativeMouseX / canvasZoom) * (zoomRatio - 1) / oldZoom
        let newScrollY = (viewportUnderMouse.scroll_y || 0) - (relativeMouseY / canvasZoom) * (zoomRatio - 1) / oldZoom
        
        // Limit scroll coordinates to reasonable bounds
        newScrollX = Math.max(-10000, Math.min(10000, newScrollX))
        newScrollY = Math.max(-10000, Math.min(10000, newScrollY))
        
        // Update viewport zoom and scroll
        setLocalViewports(prev => prev.map(v => 
          v.id === viewportUnderMouse.id 
            ? { ...v, zoom: newZoom, scroll_x: newScrollX, scroll_y: newScrollY }
            : v
        ))
        
        // Save to database
        handleUpdateViewport(viewportUnderMouse.id, {
          scroll_x: newScrollX,
          scroll_y: newScrollY,
          zoom: newZoom
        })
      } else {
        // In sheet edit mode, check viewport scroll mode
        if (viewportScrollMode[viewportUnderMouse.id]) {
          // Viewport is in scroll mode, zoom the viewport content around mouse position
          const delta = wheelEvent.deltaY > 0 ? 0.9 : 1.1
          const oldZoom = viewportUnderMouse.zoom || 1
          const newZoom = Math.max(0.1, Math.min(4, oldZoom * delta))
          
          // Calculate mouse position relative to viewport
          const viewportX = viewportUnderMouse.screen_x * canvasZoom + canvasCenter.x
          const viewportY = viewportUnderMouse.screen_y * canvasZoom + canvasCenter.y
          const relativeMouseX = mouseX - viewportX
          const relativeMouseY = mouseY - viewportY
          
          // Calculate zoom center offset - simple and reliable
          const zoomRatio = newZoom / oldZoom
          let newScrollX = (viewportUnderMouse.scroll_x || 0) - (relativeMouseX / canvasZoom) * (zoomRatio - 1) / oldZoom
          let newScrollY = (viewportUnderMouse.scroll_y || 0) - (relativeMouseY / canvasZoom) * (zoomRatio - 1) / oldZoom
          
          // Limit scroll coordinates to reasonable bounds
          newScrollX = Math.max(-10000, Math.min(10000, newScrollX))
          newScrollY = Math.max(-10000, Math.min(10000, newScrollY))
          
          setLocalViewports(prev => prev.map(v => 
            v.id === viewportUnderMouse.id 
              ? { ...v, zoom: newZoom, scroll_x: newScrollX, scroll_y: newScrollY }
              : v
          ))
          
          handleUpdateViewport(viewportUnderMouse.id, {
            scroll_x: newScrollX,
            scroll_y: newScrollY,
            zoom: newZoom
          })
        } else {
          // Viewport is in frame mode, zoom the sheet (viewport content scales with sheet)
          const delta = wheelEvent.deltaY > 0 ? 0.9 : 1.1
          const oldZoom = canvasZoom
          const newZoom = Math.max(0.1, Math.min(5, oldZoom * delta))
          
          // Calculate zoom center offset for canvas - simple and reliable
          const zoomRatio = newZoom / oldZoom
          const canvasCenterX = canvasCenter.x - (mouseX - rect.width / 2) * (zoomRatio - 1)
          const canvasCenterY = canvasCenter.y - (mouseY - rect.height / 2) * (zoomRatio - 1)
          
          setCanvasZoom(newZoom)
          setCanvasCenter({ x: canvasCenterX, y: canvasCenterY })
          
          // Save canvas state
          saveCanvasState(newZoom, { x: canvasCenterX, y: canvasCenterY })
          
          // Don't update viewport zoom - it will scale with canvasZoom automatically
        }
      }
    } else {
      // Mouse is over empty canvas area
      if (!globalZoomMode) {
        // Zoom the sheet around mouse position (viewport content scales with sheet)
        const delta = wheelEvent.deltaY > 0 ? 0.9 : 1.1
        const oldZoom = canvasZoom
        const newZoom = Math.max(0.1, Math.min(5, oldZoom * delta))
        
        // Calculate zoom center offset for canvas - simple and reliable
        const zoomRatio = newZoom / oldZoom
        const canvasCenterX = canvasCenter.x - (mouseX - rect.width / 2) * (zoomRatio - 1)
        const canvasCenterY = canvasCenter.y - (mouseY - rect.height / 2) * (zoomRatio - 1)
        
        setCanvasZoom(newZoom)
        setCanvasCenter({ x: canvasCenterX, y: canvasCenterY })
        
        // Save canvas state
        saveCanvasState(newZoom, { x: canvasCenterX, y: canvasCenterY })
        
        // Don't update viewport zoom - it will scale with canvasZoom automatically
      }
      // If globalZoomMode is true and mouse is over empty area, do nothing
    }
  }

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1) { // Middle mouse button
      e.preventDefault()
      const startX = e.clientX - canvasCenter.x
      const startY = e.clientY - canvasCenter.y
      
      const handleMouseMove = (e: MouseEvent) => {
        const newCenter = {
          x: e.clientX - startX,
          y: e.clientY - startY
        }
        setCanvasCenter(newCenter)
        // Save canvas state during drag
        saveCanvasState(canvasZoom, newCenter)
      }
      
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
  }

  const resetCanvasView = () => {
    setCanvasZoom(1)
    setCanvasCenter({ x: 0, y: 0 })
    saveCanvasState(1, { x: 0, y: 0 })
  }

  // Viewport drag handlers
  const handleViewportMouseDown = (e: React.MouseEvent, viewportId: number) => {
    // Только если глобальный режим зума выключен (режим редактирования листа)
    if (!globalZoomMode) {
      e.preventDefault()
      e.stopPropagation()
      
      const viewport = localViewports.find(v => v.id === viewportId)
      if (!viewport) return
      
      setDraggingViewport(viewportId)
      setDragStart({ x: e.clientX, y: e.clientY })
      setViewportStartPos({ x: viewport.screen_x, y: viewport.screen_y })
    }
  }

  // Viewport resize handlers
  const handleResizeMouseDown = (e: React.MouseEvent, viewportId: number, handle: string) => {
    if (!globalZoomMode) {
      e.preventDefault()
      e.stopPropagation()
      
      const viewport = localViewports.find(v => v.id === viewportId)
      if (!viewport) return
      
      setResizingViewport(viewportId)
      setResizeHandle(handle)
      setDragStart({ x: e.clientX, y: e.clientY })
      setViewportStartSize({ width: viewport.screen_width, height: viewport.screen_height })
      setViewportStartPos({ x: viewport.screen_x, y: viewport.screen_y })
    }
  }

  const handleViewportMouseMove = (e: MouseEvent) => {
    if (draggingViewport && !globalZoomMode) {
      const deltaX = (e.clientX - dragStart.x) / canvasZoom
      const deltaY = (e.clientY - dragStart.y) / canvasZoom
      
      const newX = Math.max(0, Math.min(paperSizePixels.width - 200, viewportStartPos.x + deltaX))
      const newY = Math.max(0, Math.min(paperSizePixels.height - 200, viewportStartPos.y + deltaY))
      
      setLocalViewports(prev => prev.map(v => 
        v.id === draggingViewport 
          ? { ...v, screen_x: newX, screen_y: newY }
          : v
      ))
    } else if (resizingViewport && !globalZoomMode && resizeHandle) {
      const deltaX = (e.clientX - dragStart.x) / canvasZoom
      const deltaY = (e.clientY - dragStart.y) / canvasZoom
      
      let newWidth = viewportStartSize.width
      let newHeight = viewportStartSize.height
      let newX = viewportStartPos.x
      let newY = viewportStartPos.y
      
      // Handle different resize directions
      if (resizeHandle.includes('e')) { // East (right)
        newWidth = Math.max(100, viewportStartSize.width + deltaX)
      }
      if (resizeHandle.includes('w')) { // West (left)
        newWidth = Math.max(100, viewportStartSize.width - deltaX)
        newX = viewportStartPos.x + deltaX
      }
      if (resizeHandle.includes('s')) { // South (bottom)
        newHeight = Math.max(100, viewportStartSize.height + deltaY)
      }
      if (resizeHandle.includes('n')) { // North (top)
        newHeight = Math.max(100, viewportStartSize.height - deltaY)
        newY = viewportStartPos.y + deltaY
      }
      
      // Ensure viewport stays within paper bounds
      newX = Math.max(0, Math.min(paperSizePixels.width - newWidth, newX))
      newY = Math.max(0, Math.min(paperSizePixels.height - newHeight, newY))
      newWidth = Math.min(paperSizePixels.width - newX, newWidth)
      newHeight = Math.min(paperSizePixels.height - newY, newHeight)
      
      setLocalViewports(prev => prev.map(v => 
        v.id === resizingViewport 
          ? { ...v, screen_x: newX, screen_y: newY, screen_width: newWidth, screen_height: newHeight }
          : v
      ))
    }
  }

  const handleViewportMouseUp = () => {
    if (draggingViewport) {
      // Calculate final position based on drag delta
      const currentViewport = localViewports.find(v => v.id === draggingViewport)
      if (currentViewport) {
        // Calculate new position based on current mouse position and drag start
        const mouseEvent = window.event as MouseEvent
        if (mouseEvent) {
          const deltaX = (mouseEvent.clientX - dragStart.x) / canvasZoom
          const deltaY = (mouseEvent.clientY - dragStart.y) / canvasZoom
          
          const newX = Math.max(0, Math.min(paperSizePixels.width - 200, viewportStartPos.x + deltaX))
          const newY = Math.max(0, Math.min(paperSizePixels.height - 200, viewportStartPos.y + deltaY))
          
          
          // Save to database with calculated position
          updateViewportMutation.mutate({ 
            id: currentViewport.id, 
            data: {
              screen_x: newX,
              screen_y: newY,
            }
          }, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
          },
          onError: (error) => {
            console.error('Failed to save viewport position:', error)
            queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
          }
        })
        } else {
          // Fallback: use current viewport position if mouse event is not available
          updateViewportMutation.mutate({ 
            id: currentViewport.id, 
            data: {
              screen_x: currentViewport.screen_x,
              screen_y: currentViewport.screen_y,
            }
          }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
            },
            onError: (error) => {
              console.error('Failed to save viewport position (fallback):', error)
              queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
            }
          })
        }
      }
      
      setDraggingViewport(null)
    } else if (resizingViewport) {
      // Calculate final size based on resize delta
      const currentViewport = localViewports.find(v => v.id === resizingViewport)
      if (currentViewport && resizeHandle) {
        // Calculate new size based on current mouse position and resize start
        const mouseEvent = window.event as MouseEvent
        if (mouseEvent) {
          const deltaX = (mouseEvent.clientX - dragStart.x) / canvasZoom
          const deltaY = (mouseEvent.clientY - dragStart.y) / canvasZoom
          
          let newWidth = viewportStartSize.width
          let newHeight = viewportStartSize.height
          let newX = viewportStartPos.x
          let newY = viewportStartPos.y
          
          // Handle different resize directions
          if (resizeHandle.includes('e')) { // East (right)
            newWidth = Math.max(100, viewportStartSize.width + deltaX)
          }
          if (resizeHandle.includes('w')) { // West (left)
            newWidth = Math.max(100, viewportStartSize.width - deltaX)
            newX = viewportStartPos.x + deltaX
          }
          if (resizeHandle.includes('s')) { // South (bottom)
            newHeight = Math.max(100, viewportStartSize.height + deltaY)
          }
          if (resizeHandle.includes('n')) { // North (top)
            newHeight = Math.max(100, viewportStartSize.height - deltaY)
            newY = viewportStartPos.y + deltaY
          }
          
          // Ensure viewport stays within paper bounds
          newX = Math.max(0, Math.min(paperSizePixels.width - newWidth, newX))
          newY = Math.max(0, Math.min(paperSizePixels.height - newHeight, newY))
          newWidth = Math.min(paperSizePixels.width - newX, newWidth)
          newHeight = Math.min(paperSizePixels.height - newY, newHeight)
          
          
          // Save to database with calculated size
          updateViewportMutation.mutate({ 
            id: currentViewport.id, 
            data: {
              screen_x: newX,
              screen_y: newY,
              screen_width: newWidth,
              screen_height: newHeight,
            }
          }, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
          },
          onError: (error) => {
            console.error('Failed to save viewport size:', error)
            queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
          }
        })
        } else {
          // Fallback: use current viewport size if mouse event is not available
          updateViewportMutation.mutate({ 
            id: currentViewport.id, 
            data: {
              screen_x: currentViewport.screen_x,
              screen_y: currentViewport.screen_y,
              screen_width: currentViewport.screen_width,
              screen_height: currentViewport.screen_height,
            }
          }, {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
            },
            onError: (error) => {
              console.error('Failed to save viewport size (fallback):', error)
              queryClient.invalidateQueries({ queryKey: ['print-viewports', sheetId] })
            }
          })
        }
      }
      
      setResizingViewport(null)
      setResizeHandle(null)
    }
  }


  // Queries
  const { data: sheet, isLoading: sheetLoading } = useQuery({
    queryKey: ['print-sheet', sheetId],
    queryFn: () => getPrintSheet(Number(sheetId!)),
    enabled: !!sheetId,
  })

  const { data: viewports = [], isLoading: viewportsLoading } = useQuery({
    queryKey: ['print-viewports', sheetId],
    queryFn: () => getPrintViewports(Number(sheetId!)),
    enabled: !!sheetId,
  })

  const { data: stamps = [] } = useQuery({
    queryKey: ['print-stamps'],
    queryFn: getPrintStamps,
  })

  const { data: presets = [] } = useQuery({
    queryKey: ['print-presets'],
    queryFn: getPrintPresets,
  })

  // Sync local state with API data and restore viewport from localStorage
  useEffect(() => {
    if (viewports) {
      // For new architecture, restore viewport from localStorage
      if (isNewArchitecture) {
        const restoredViewports = viewports.map((viewport: any) => {
          const savedViewport = restoreViewportFromStorage(viewport.id)
          if (savedViewport) {
            return {
              ...viewport,
              scroll_x: savedViewport.scroll_x,
              scroll_y: savedViewport.scroll_y,
              zoom: savedViewport.zoom
            }
          }
          return viewport
        })
        setLocalViewports(restoredViewports)
      } else {
        setLocalViewports(viewports)
      }
    }
  }, [viewports, isNewArchitecture, restoreViewportFromStorage])

  // Restore canvas state on mount (only once)
  useEffect(() => {
    restoreCanvasState()
  }, []) // Empty dependency array - only run once on mount

  // Mutations
  const updateSheetMutation = useMutation({
    mutationFn: updatePrintSheet,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheet', sheetId] })
    },
  })

  const updateViewportMutation = useMutation({
    mutationFn: updatePrintViewport,
    onSuccess: () => {
      // Don't invalidate queries for drag/resize operations to avoid conflicts with local state
      setEditingViewport(null)
    },
  })

  const deleteViewportMutation = useMutation({
    mutationFn: deletePrintViewport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheet', sheetId] })
    },
  })

  const applyPresetMutation = useMutation({
    mutationFn: applyPreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-sheet', sheetId] })
    },
  })

  const savePresetMutation = useMutation({
    mutationFn: savePreset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-presets'] })
      setIsSavePresetModalOpen(false)
      setPresetName('')
    },
  })

  const handleUpdateSheet = (updates: Partial<PrintSheet>) => {
    if (!sheet) return
    updateSheetMutation.mutate({ id: sheet.id, data: updates })
  }

  const handleUpdateViewport = (viewportId: number, updates: Partial<PrintViewport>) => {
    // Sanitize incoming values to avoid corrupt state
    const clamp = (value: number, min: number, max: number) => {
      if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return min;
      return Math.max(min, Math.min(max, value));
    };
    const MIN_SCALE = 0.01;
    const MAX_SCALE = 20;
    const CONTENT_SIZE = 100000; // must match SimpleViewport

    // Find current viewport to know width/height
    const current = localViewports.find(v => v.id === viewportId);
    const scale = clamp(updates.zoom ?? (current?.zoom || 1), MIN_SCALE, MAX_SCALE);
    // Allow symmetric wide bounds; backend clamps appropriately
    const minX = -CONTENT_SIZE;
    const maxX = CONTENT_SIZE;
    const minY = -CONTENT_SIZE;
    const maxY = CONTENT_SIZE;

    const safeUpdates: Partial<PrintViewport> = {
      ...updates,
      zoom: scale,
      scroll_x: clamp(updates.scroll_x ?? (current?.scroll_x || 0), minX, maxX),
      scroll_y: clamp(updates.scroll_y ?? (current?.scroll_y || 0), minY, maxY),
    };

    // Save to database first, then update local state on success
    updateViewportMutation.mutate({ 
      id: viewportId, 
      data: safeUpdates 
    }, {
      onSuccess: () => {
        // Update local state only after successful save
        setLocalViewports(prev => 
          prev.map(viewport => 
            viewport.id === viewportId 
              ? { ...viewport, ...safeUpdates }
              : viewport
          )
        )
        
        // Also save to localStorage for new architecture
        if (isNewArchitecture && safeUpdates.scroll_x !== undefined && safeUpdates.scroll_y !== undefined && safeUpdates.zoom !== undefined) {
          saveViewportToStorage(viewportId, {
            scroll_x: safeUpdates.scroll_x,
            scroll_y: safeUpdates.scroll_y,
            zoom: safeUpdates.zoom
          })
        }
      }
    })
  }

  // Apply local updates immediately for smooth UX, then debounce save to backend
  const applyViewportLocally = (viewportId: number, updates: Partial<PrintViewport>) => {
    setLocalViewports(prev => prev.map(v => v.id === viewportId ? { ...v, ...updates } : v))
  }

  const scheduleViewportSave = (viewportId: number, updates: Partial<PrintViewport>) => {
    const timers = viewportSaveTimers.current
    const existing = timers.get(viewportId)
    if (existing) clearTimeout(existing)
    const timer = setTimeout(() => {
      handleUpdateViewport(viewportId, updates)
      // Also save to localStorage for new architecture
      if (isNewArchitecture && updates.scroll_x !== undefined && updates.scroll_y !== undefined && updates.zoom !== undefined) {
        saveViewportToStorage(viewportId, {
          scroll_x: updates.scroll_x,
          scroll_y: updates.scroll_y,
          zoom: updates.zoom
        })
      }
      timers.delete(viewportId)
    }, 200)
    timers.set(viewportId, timer)
  }

  const handleDeleteViewport = (viewportId: number) => {
    deleteViewportMutation.mutate(viewportId)
  }

  // Add wheel event listener for canvas zoom
  useEffect(() => {
    const canvasElement = document.querySelector('.canvas-container')
    if (canvasElement) {
      const wheelHandler = (e: Event) => {
        e.preventDefault()
        // Ignore wheel events originating inside a SimpleViewport (new architecture)
        const target = e.target as HTMLElement
        let el: HTMLElement | null = target
        let insideViewport = false
        for (let i = 0; i < 6 && el; i++) {
          if (el.classList && el.classList.contains('viewport-container')) {
            insideViewport = true
            break
          }
          el = el.parentElement
        }
        if (!insideViewport) {
          handleCanvasWheel(e)
        }
      }
      canvasElement.addEventListener('wheel', wheelHandler, { passive: false })
      return () => {
        canvasElement.removeEventListener('wheel', wheelHandler)
      }
    }
  }, [globalZoomMode, canvasZoom, canvasCenter, localViewports, viewportScrollMode, handleUpdateViewport])


  // Helper functions
  const mmToPixels = (mm: number) => {
    return Math.round((mm / 25.4) * selectedDPI)
  }

  // Calculate paper dimensions based on format and orientation
  const paperDimensions = useMemo(() => {
    if (!sheet) return { width: 210, height: 297 }
    
    const format = PAPER_FORMATS[sheet.format] || PAPER_FORMATS.A4
    if (sheet.orientation === 'landscape') {
      return { width: format.height, height: format.width }
    }
    return format
  }, [sheet])

  // Calculate paper size in pixels for display
  const paperSizePixels = useMemo(() => {
    return {
      width: mmToPixels(paperDimensions.width),
      height: mmToPixels(paperDimensions.height)
    }
  }, [paperDimensions, selectedDPI])

  // Add global mouse event listeners for viewport dragging and resizing
  useEffect(() => {
    if (draggingViewport || resizingViewport) {
      document.addEventListener('mousemove', handleViewportMouseMove)
      document.addEventListener('mouseup', handleViewportMouseUp)
      
      return () => {
        document.removeEventListener('mousemove', handleViewportMouseMove)
        document.removeEventListener('mouseup', handleViewportMouseUp)
      }
    }
  }, [draggingViewport, resizingViewport, dragStart, viewportStartPos, viewportStartSize, canvasZoom, paperSizePixels, resizeHandle])


  const handleExportPDF = async () => {
    if (!sheet) return
    
    setIsExporting(true)
    try {
      // Export logic here
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  if (sheetLoading || viewportsLoading) {
    return <div className="text-center py-8 text-gray-600">Загрузка...</div>
  }

  if (!sheet) {
    return <div className="text-center py-8 text-red-600">Лист не найден</div>
  }

  return (
    <div className="h-screen flex bg-gray-100 overflow-hidden">
        {/* Left panel - Settings */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            
            {/* 0. Навигация и управление */}
            <div className="border-b border-gray-200 pb-6">
              <div className="space-y-4">
                {/* Навигация */}
            <div className="flex items-center space-x-4">
              <Link 
                to={`/projects/${projectId}/prints`} 
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                ← Назад к листам печати
              </Link>
              <div className="text-gray-300">|</div>
              <Link 
                to={`/projects/${projectId}`} 
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Проект
              </Link>
            </div>
                
                {/* Название листа */}
                <div>
                  <h1 className="text-lg font-bold text-gray-900">
                    {sheet.name}
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {sheet.format} {sheet.orientation === 'portrait' ? 'Книжная' : 'Альбомная'} ориентация
                  </p>
                </div>
                
                {/* DPI и экспорт */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DPI
                    </label>
              <select
                value={selectedDPI}
                onChange={(e) => setSelectedDPI(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {DPI_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
                  </div>
                  
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
              >
                {isExporting ? 'Экспорт...' : '🖨️ Экспорт PDF'}
              </button>
            </div>
        </div>
      </div>

            {/* 1. Настройки листа */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. Настройки листа</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название листа
                  </label>
                  <input
                    type="text"
                    value={sheet.name}
                    onChange={(e) => handleUpdateSheet({ name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Формат
                  </label>
                  <select
                    value={sheet.format}
                    onChange={(e) => handleUpdateSheet({ format: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.keys(PAPER_FORMATS).map(format => (
                      <option key={format} value={format}>{format}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ориентация
                  </label>
                  <select
                    value={sheet.orientation}
                    onChange={(e) => handleUpdateSheet({ orientation: e.target.value as 'portrait' | 'landscape' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="portrait">Книжная</option>
                    <option value="landscape">Альбомная</option>
                  </select>
                </div>
              </div>
                </div>

            {/* 1.5. Глобальный режим зума */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1.5. Режим взаимодействия</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm font-medium text-gray-700">
                      Режим взаимодействия
                    </div>
                    <div className="text-xs text-gray-500">
                      {globalZoomMode 
                        ? 'Скролл зумирует схемы внутри viewportов' 
                        : 'Скролл зумирует лист, можно перетаскивать viewportы'
                      }
                    </div>
                  </div>
                  <button
                    onClick={() => setGlobalZoomMode(!globalZoomMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      globalZoomMode ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        globalZoomMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  </div>
                </div>
            </div>

            {/* 2. Вьюпорты */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. Вьюпорты</h3>
              <div className="space-y-3">
                {localViewports.map(viewport => (
                  <div key={viewport.id} className="bg-gray-50 p-3 rounded border">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          {viewport.name || `Viewport ${viewport.id}`}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {viewport.schema_type} схема
                        </p>
                        <p className="text-xs text-gray-500">
                          {viewport.screen_width}×{viewport.screen_height} px
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => toggleViewportScrollMode(viewport.id)}
                          className={`text-xs px-2 py-1 rounded ${
                            viewportScrollMode[viewport.id] 
                              ? 'bg-green-600 text-white' 
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {viewportScrollMode[viewport.id] ? 'Скролл' : 'Рамка'}
                        </button>
                        <button
                          onClick={() => setEditingViewport(viewport)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteViewport(viewport.id)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 rounded-md transition-colors">
                  + Добавить viewport
                </button>
              </div>
            </div>

            {/* 3. Информация */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. Информация</h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Размер:</span> {paperDimensions.width}×{paperDimensions.height} мм</p>
                <p><span className="font-medium">В пикселях:</span> {paperSizePixels.width}×{paperSizePixels.height} px</p>
                <p><span className="font-medium">DPI:</span> {selectedDPI}</p>
                <p><span className="font-medium">Вьюпортов:</span> {localViewports.length}</p>
              </div>
              
              {/* Test new architecture toggle */}
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isNewArchitecture}
                    onChange={(e) => {
                      if (e.target.checked) {
                        window.location.hash = 'new-architecture';
                      } else {
                        window.location.hash = '';
                      }
                      window.location.reload();
                    }}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm font-medium">Тестировать новую архитектуру</span>
                </label>
              </div>
            </div>

            {/* 4. Штамп */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">4. Штамп</h3>
              <div className="space-y-3">
                  <select
                    value={selectedStamp?.id || ''}
                    onChange={(e) => {
                      const stamp = stamps.find((s: PrintStamp) => s.id === Number(e.target.value))
                    setSelectedStamp(stamp || null)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                  <option value="">Без штампа</option>
                    {stamps.map((stamp: PrintStamp) => (
                      <option key={stamp.id} value={stamp.id}>{stamp.name}</option>
                    ))}
                  </select>
                {selectedStamp && (
                  <div className="text-xs text-gray-600">
                    {selectedStamp.width_mm}×{selectedStamp.height_mm} мм
                  </div>
                )}
              </div>
            </div>

            {/* 5. Пресеты */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. Пресеты</h3>
              <div className="space-y-3">
                {presets.map((preset: any) => (
                  <div key={preset.id} className="flex justify-between items-center">
                    <span className="text-sm">{preset.name}</span>
                <button
                      onClick={() => applyPresetMutation.mutate({ sheetId: sheet.id, presetId: preset.id })}
                      className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                      Применить
                </button>
              </div>
                ))}
                  <button
                  onClick={() => setIsSavePresetModalOpen(true)}
                  className="w-full py-2 border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-md transition-colors text-sm"
                  >
                  💾 Сохранить как пресет
                  </button>
            </div>
          </div>

                </div>
              </div>

      {/* Right panel - Canvas */}
      <div 
        className="flex-1 bg-gray-200 relative overflow-hidden canvas-container"
        onMouseDown={handleCanvasMouseDown}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Canvas container with zoom */}
          <div 
            className="bg-white shadow-lg"
                style={{
              width: paperSizePixels.width * canvasZoom,
              height: paperSizePixels.height * canvasZoom,
              transform: `translate(${canvasCenter.x}px, ${canvasCenter.y}px)`,
              position: 'relative'
            }}
          >
            {/* Paper content */}
            <div className="relative w-full h-full border border-gray-300">
              {/* Viewports */}
              {localViewports.map(viewport => {
                const clamp = (value: number, min: number, max: number) => {
                  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) return min;
                  return Math.max(min, Math.min(max, value));
                };
                const MIN_SCALE = 0.1;
                const MAX_SCALE = 10;
                const CONTENT_SIZE = 100000; // must match SimpleViewport
                const vw = Number(viewport.screen_width) || 200;
                const vh = Number(viewport.screen_height) || 200;
                const rawScale = Number((viewport as any).zoom ?? 1);
                const safeScale = clamp(Number.isFinite(rawScale) && !Number.isNaN(rawScale) ? rawScale : 1, MIN_SCALE, MAX_SCALE);
                // Compute dynamic bounds so content always intersects viewport, but allow some overscroll
                const minX = -CONTENT_SIZE;
                const maxX = CONTENT_SIZE;
                const minY = -CONTENT_SIZE;
                const maxY = CONTENT_SIZE;
                const rawX = Number((viewport as any).scroll_x ?? 0);
                const rawY = Number((viewport as any).scroll_y ?? 0);
                const safeX = clamp(Number.isFinite(rawX) && !Number.isNaN(rawX) ? rawX : 0, minX, maxX);
                const safeY = clamp(Number.isFinite(rawY) && !Number.isNaN(rawY) ? rawY : 0, minY, maxY);

                return (
                  <div
                    key={viewport.id}
                  className={`absolute border-2 bg-white ${
                    draggingViewport === viewport.id 
                      ? 'border-red-500 shadow-lg' 
                      : globalZoomMode 
                        ? 'border-blue-500' 
                        : 'border-blue-500 cursor-move'
                  }`}
                    style={{
                    left: viewport.screen_x * canvasZoom,
                    top: viewport.screen_y * canvasZoom,
                    width: viewport.screen_width * canvasZoom,
                    height: viewport.screen_height * canvasZoom,
                      zIndex: viewport.z_index,
                    }}
                  onMouseDown={(e) => handleViewportMouseDown(e, viewport.id)}
                  >
                  {/* Viewport header */}
                    <div className="absolute -top-6 left-0 text-xs text-blue-600 bg-blue-100 px-1 rounded">
                      {viewport.schema_type} {viewport.name ? `- ${viewport.name}` : ''}
                    </div>
                    
                  {/* Viewport content */}
                  <div className="absolute inset-0 overflow-hidden">
                    {isNewArchitecture ? (
                      /* New architecture - simple viewport with CSS transform */
                      <SimpleViewport
                        x={safeX}
                        y={safeY}
                        width={vw}
                        height={vh}
                        scale={safeScale}
                        outerScale={canvasZoom}
                        noTransform={true}
                        isInteractive={globalZoomMode || (viewportScrollMode[viewport.id] || false)}
                        bounds={schemaBounds[viewport.id]}
                        onViewportChange={(updates) => {
                          // Optimistic local update + debounced save
                          if (viewport.id) {
                            applyViewportLocally(viewport.id, {
                              scroll_x: updates.x,
                              scroll_y: updates.y,
                              zoom: updates.scale,
                            })
                            scheduleViewportSave(viewport.id, {
                              scroll_x: updates.x,
                              scroll_y: updates.y,
                              zoom: updates.scale,
                            })
                          }
                        }}
                      >
                        <FullSchemaRenderer
                          schemaType={viewport.schema_type as 'main' | 'terminal'}
                          projectId={projectId!}
                          schemaId={viewport.schema_type === 'main' ? Number(1) : undefined}
                          blockId={viewport.schema_type === 'terminal' ? (viewport.block_id || 1) : undefined}
                          showBackground={false}
                          viewportId={viewport.id}
                          scrollX={safeX}
                          scrollY={safeY}
                          zoom={safeScale}
                          onViewportChange={(vpId, updates) => {
                            if (!vpId) return;
                            // Оптимистично в память + debounce сохранение
                            applyViewportLocally(vpId, {
                              scroll_x: updates.scroll_x,
                              scroll_y: updates.scroll_y,
                              zoom: updates.zoom,
                            })
                            scheduleViewportSave(vpId, {
                              scroll_x: updates.scroll_x,
                              scroll_y: updates.scroll_y,
                              zoom: updates.zoom,
                            })
                          }}
                          onBoundsComputed={(vpId, b) => {
                            if (!vpId) return;
                            setSchemaBounds(prev => ({ ...prev, [vpId]: b }))
                            // Авто-fit один раз при появлении границ и если скролл/зум пустые
                            const v = viewport
                            const already = autoFittedRef.current.has(vpId)
                            if (!already) {
                              // Consider any non-null value (including strings from API) as user-defined
                              const hasUserScroll = v.scroll_x != null || v.scroll_y != null || v.zoom != null
                              if (!hasUserScroll) {
                                const padding = 100
                                const worldWidth = (b.maxX - b.minX) + padding * 2 || 1
                                const worldHeight = (b.maxY - b.minY) + padding * 2 || 1
                                const fitScale = Math.max(0.01, Math.min(20, Math.min(vw / worldWidth, vh / worldHeight)))
                                applyViewportLocally(vpId, { scroll_x: b.minX - padding, scroll_y: b.minY - padding, zoom: fitScale })
                                scheduleViewportSave(vpId, { scroll_x: b.minX - padding, scroll_y: b.minY - padding, zoom: fitScale })
                                autoFittedRef.current.add(vpId)
                              }
                            }
                          }}
                        />
                      </SimpleViewport>
                    ) : (
                      /* Old architecture */
                      <PrintCanvas
                        schemaType={viewport.schema_type as 'main' | 'terminal'}
                        projectId={projectId!}
                        schemaId={viewport.schema_type === 'main' ? Number(52) : undefined}
                        blockId={viewport.schema_type === 'terminal' ? (viewport.block_id || 1) : undefined}
                        viewport={{
                          worldX: safeX,
                          worldY: safeY,
                          worldWidth: (vw) / (safeScale || 1),
                          worldHeight: (vh) / (safeScale || 1),
                          scale: safeScale,
                          screenWidth: vw,
                          screenHeight: vh,
                        }}
                        printMode={true}
                        showBackground={false}
                        className="w-full h-full"
                        viewportId={viewport.id}
                        onViewportChange={handleUpdateViewport}
                        globalZoomMode={globalZoomMode}
                      />
                    )}
                  </div>
                    
                  {/* Invisible resize handles - only show when not in global zoom mode */}
                  {!globalZoomMode && (
                    <>
                      {/* Corner handles */}
                      <div
                        className="absolute cursor-nw-resize"
                        style={{ top: -5, left: -5, width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'nw')}
                      />
                      <div
                        className="absolute cursor-ne-resize"
                        style={{ top: -5, right: -5, width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'ne')}
                    />
                    <div
                        className="absolute cursor-sw-resize"
                        style={{ bottom: -5, left: -5, width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'sw')}
                    />
                    <div
                        className="absolute cursor-se-resize"
                        style={{ bottom: -5, right: -5, width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'se')}
                      />
                      
                      {/* Edge handles */}
                      <div
                        className="absolute cursor-n-resize"
                        style={{ top: -5, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'n')}
                      />
                      <div
                        className="absolute cursor-s-resize"
                        style={{ bottom: -5, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 's')}
                      />
                      <div
                        className="absolute cursor-w-resize"
                        style={{ left: -5, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'w')}
                      />
                      <div
                        className="absolute cursor-e-resize"
                        style={{ right: -5, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10 }}
                        onMouseDown={(e) => handleResizeMouseDown(e, viewport.id, 'e')}
                      />
                    </>
                  )}
                </div>
                )
              })}

              {/* Paper info overlay */}
              <div className="absolute top-2 left-2 text-xs text-gray-500 bg-white bg-opacity-80 px-2 py-1 rounded">
                {sheet.format} {sheet.orientation === 'portrait' ? 'Книжная' : 'Альбомная'}
                <br />
                {paperDimensions.width}×{paperDimensions.height} мм
              </div>
            </div>
          </div>

          {/* Canvas zoom controls */}
          <div className="absolute bottom-4 right-4 flex space-x-2">
            <button
              onClick={() => {
                const newZoom = Math.max(0.1, canvasZoom - 0.1)
                setCanvasZoom(newZoom)
                saveCanvasState(newZoom, canvasCenter)
              }}
              className="px-3 py-2 bg-white border border-gray-300 rounded shadow hover:bg-gray-50"
            >
              -
            </button>
            <span className="px-3 py-2 bg-white border border-gray-300 rounded shadow text-sm">
              {Math.round(canvasZoom * 100)}%
                      </span>
                        <button
              onClick={() => {
                const newZoom = Math.min(3, canvasZoom + 0.1)
                setCanvasZoom(newZoom)
                saveCanvasState(newZoom, canvasCenter)
              }}
              className="px-3 py-2 bg-white border border-gray-300 rounded shadow hover:bg-gray-50"
            >
              +
            </button>
                        <button
              onClick={resetCanvasView}
              className="px-3 py-2 bg-white border border-gray-300 rounded shadow hover:bg-gray-50 text-sm"
                        >
              100%
                        </button>
                      </div>
              </div>
            </div>

      {/* Modal - Save Preset */}
      {isSavePresetModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Сохранить пресет</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название пресета
                </label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="Например: A4 Основная схема"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
                </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setIsSavePresetModalOpen(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  if (presetName.trim()) {
                    savePresetMutation.mutate({ name: presetName.trim(), sheetId: sheet.id })
                  }
                }}
                disabled={!presetName.trim() || savePresetMutation.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {savePresetMutation.isPending ? '🔄 Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Edit Viewport */}
      {editingViewport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Редактировать viewport</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название (необязательно)
                </label>
                <input
                  type="text"
                  value={editingViewport.name || ''}
                  onChange={(e) => setEditingViewport({ ...editingViewport, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                  Тип схемы
                  </label>
                <select
                  value={editingViewport.schema_type}
                  onChange={(e) => setEditingViewport({ ...editingViewport, schema_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="main">Основная схема</option>
                  <option value="terminal">Терминальная схема</option>
                  <option value="knx">KNX схема</option>
                  <option value="rs485">RS485 схема</option>
                </select>
                </div>
                </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setEditingViewport(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => {
                  handleUpdateViewport(editingViewport.id, editingViewport)
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
